import { Box, Typography } from "@mui/material";
import {
  Done,
  Close,
  HelpOutline,
  HourglassEmpty,
  Payments,
  Pending,
  ReceiptLong,
  Sync,
} from "@mui/icons-material";
import type { PaymentStatusType } from "../types/common";
import { PAYMENT_STATUS } from "../types/common";

type StatusMeta = {
  label: string;
  color: string;
  bgColor: string;
  borderColor?: string;
  IconComponent: React.ElementType;
};

const STATUS_META: Record<PaymentStatusType, StatusMeta> = {
  [PAYMENT_STATUS.DRAFT]: {
    label: "Pending Review",
    color: "#F19E2B",
    bgColor: "#FFF8E1",
    IconComponent: HourglassEmpty,
  },
  [PAYMENT_STATUS.SUBMITTED]: {
    label: "Submitted",
    color: "#1976D2",
    bgColor: "#E3F2FD",
    IconComponent: Sync,
  },
  [PAYMENT_STATUS.UNDER_REVIEW]: {
    label: "Under Review",
    color: "#FFFFFF",
    bgColor: "#F19E2B",
    IconComponent: Pending,
  },
  [PAYMENT_STATUS.APPROVED]: {
    label: "Approved",
    color: "#387E47",
    bgColor: "#EDF5EE",
    borderColor: "#DAECDE",
    IconComponent: Done,
  },
  [PAYMENT_STATUS.PAYMENT_RECORDED]: {
    label: "Payment Recorded",
    color: "#0277BD",
    bgColor: "#E1F5FE",
    IconComponent: ReceiptLong,
  },
  [PAYMENT_STATUS.PAID]: {
    label: "Paid",
    color: "#FFFFFF",
    bgColor: "#469E59",
    IconComponent: Payments,
  },
  [PAYMENT_STATUS.REJECTED]: {
    label: "Rejected",
    color: "#DB382A",
    bgColor: "#FBEBEA",
    borderColor: "#F8D7D4",
    IconComponent: Close,
  },
};

const FALLBACK_META: StatusMeta = {
  label: "Unknown",
  color: "#666",
  bgColor: "#F5F5F5",
  IconComponent: HelpOutline,
};

interface PaymentStatusBadgeProps {
  status: PaymentStatusType;
}

export default function PaymentStatusBadge({ status }: PaymentStatusBadgeProps) {
  const meta = STATUS_META[status] ?? FALLBACK_META;
  const { label, color, bgColor, borderColor, IconComponent } = meta;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        pl: "4px",
        pr: "8px",
        py: "4px",
        borderRadius: "4px",
        color,
        backgroundColor: bgColor,
        border: borderColor ? `1px solid ${borderColor}` : "none",
        whiteSpace: "nowrap",
      }}
    >
      <IconComponent sx={{ fontSize: 12 }} />
      <Typography
        component="span"
        sx={{
          color: "inherit",
          fontSize: "10px",
          fontWeight: 500,
          lineHeight: "12px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
